import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { AvatarSprite } from '../components/avatar/AvatarSprite';
import { TrendingUp, BarChart3, Shield, BookOpen } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';

interface ChartDataPoint {
  date: string;
  brkb: number;
  spy: number;
}

const formatDollar = (value: number) =>
  `$${value.toLocaleString()}`;

const ReturnLabel = ({ viewBox, value, color }: { viewBox?: { x: number; y: number }; value: string; color: string }) => {
  if (!viewBox) return null;
  const { x, y } = viewBox;
  return (
    <g>
      <polygon
        points={`${x + 8},${y} ${x + 14},${y - 5} ${x + 14},${y + 5}`}
        fill={color}
      />
      <text
        x={x + 18}
        y={y - 7}
        fill={color}
        fontSize={11}
        fontWeight={700}
        dominantBaseline="central"
      >
        {value}
      </text>
      <text
        x={x + 18}
        y={y + 7}
        fill={color}
        fontSize={9}
        opacity={0.7}
        dominantBaseline="central"
      >
        Total Return
      </text>
    </g>
  );
};

const formatTooltipDate = (date: string) => {
  const d = new Date(date + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

export default function BaronWuffettPage() {
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);

  useEffect(() => {
    fetch('/data/brkb-vs-spy.json')
      .then(res => res.json())
      .then(json => setChartData(json.data))
      .catch(() => {});
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Baron Wuffett - Stock Analyst | Wallstreetle",
    "description": "Meet Baron Wuffett, Wallstreetle's stock analyst who breaks down daily mystery stocks with value investing insights.",
    "url": "https://wallstreetle.com/baron-wuffett",
    "inLanguage": "en-US",
    "isPartOf": {
      "@type": "WebSite",
      "name": "Wallstreetle",
      "url": "https://wallstreetle.com"
    },
    "about": {
      "@type": "Thing",
      "name": "Baron Wuffett",
      "description": "Wallstreetle's resident stock analyst, inspired by value investing principles. Provides insightful breakdowns of daily mystery stocks."
    },
    "publisher": {
      "@type": "Organization",
      "name": "Wallstreetle",
      "logo": {
        "@type": "ImageObject",
        "url": "https://wallstreetle.com/logo512.png",
        "width": 512,
        "height": 512
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>Wallstreetle | Baron Wuffett</title>
        <meta name="description" content="Meet Baron Wuffett, Wallstreetle's stock analyst who breaks down daily mystery stocks with value investing insights. Learn about price action, financials, and business quality." />
        <meta name="keywords" content="baron wuffett, wallstreetle baron wuffett, stock analyst, stock analysis, value investing, wallstreetle" />
        <link rel="canonical" href="https://wallstreetle.com/baron-wuffett" />

        {/* Open Graph */}
        <meta property="og:title" content="Wallstreetle | Baron Wuffett" />
        <meta property="og:description" content="Meet Baron Wuffett, Wallstreetle's resident stock analyst. Get value investing insights and detailed breakdowns of daily mystery stocks." />
        <meta property="og:url" content="https://wallstreetle.com/baron-wuffett" />
        <meta property="og:image" content="https://wallstreetle.com/assets/baron-wuffett/baron-wuffett-vector.svg" />
        <meta property="og:type" content="website" />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background dark:bg-gray-900 py-12 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Hero */}
          <div className="text-center mb-16">
            <div className="flex justify-center mb-6">
              <img
                src="/assets/baron-wuffett/baron-wuffett-vector.svg"
                alt="Baron Wuffett, Wallstreetle's stock analyst"
                className="w-40 h-40 md:w-52 md:h-52"
              />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Baron Wuffett
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Wallstreetle's resident stock analyst, inspired by the principles of value investing
            </p>
          </div>

          {/* Who is Baron Wuffett? */}
          <Card className="p-8 mb-8">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <AvatarSprite gesture="baron-wuffett-smile" size={120} />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-3">Who is Baron Wuffett?</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Baron Wuffett is Wallstreetle's resident stock analyst. Dressed in medieval baron attire and armed with an unwavering commitment to fundamental analysis, he brings value investing principles to every daily mystery stock.
                </p>
                <p className="text-gray-700 dark:text-gray-300">
                  After you solve each day's puzzle, Baron Wuffett steps in with an insightful breakdown of the stock, walking you through what makes the company tick, how it's performing financially, and whether its valuation tells a compelling story.
                </p>
              </div>
            </div>
          </Card>

          {/* What does Baron Wuffett do? */}
          <Card className="p-8 mb-8">
            <div className="flex flex-col md:flex-row-reverse items-start gap-6">
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <AvatarSprite gesture="baron-wuffett-reading" size={120} />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-3">What Does Baron Wuffett Do?</h2>
                <p className="text-gray-700 dark:text-gray-300 mb-3">
                  Once you've identified the daily mystery stock, Baron Wuffett delivers a detailed analysis covering five key dimensions:
                </p>
                <ul className="space-y-2 text-gray-700 dark:text-gray-300">
                  <li className="flex items-start gap-2">
                    <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Price Action</strong> &mdash; What the chart reveals about recent momentum, trends, and volatility</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <BarChart3 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Business Quality</strong> &mdash; Revenue growth, market position, and competitive advantages</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <TrendingUp className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Profitability</strong> &mdash; Margins, earnings quality, and return on capital</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <BarChart3 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Valuation</strong> &mdash; Whether the stock price reflects intrinsic value or offers a margin of safety</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Shield className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span><strong>Balance Sheet Strength</strong> &mdash; Debt levels, cash reserves, and financial resilience</span>
                  </li>
                </ul>
                <p className="text-gray-700 dark:text-gray-300 mt-3">
                  Each step spotlights the relevant financial data on screen, turning a puzzle game into a hands-on investing lesson.
                </p>
              </div>
            </div>
          </Card>

          {/* Baron Wuffett's Philosophy */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-center mb-8">Baron Wuffett's Philosophy</h2>
            <div className="grid gap-6 md:grid-cols-2">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Long-Term Focus</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Baron Wuffett evaluates companies the way a long-term owner would. Short-term noise matters less than durable business fundamentals and multi-year growth trajectories.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Durable Competitive Advantages</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      He looks for moats &mdash; brand strength, network effects, switching costs, and scale advantages that protect a company's earnings over time.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                    <BarChart3 className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Conservative Analysis</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Baron Wuffett keeps it grounded. He favors facts over hype, examines real financial data, and isn't afraid to point out risks alongside opportunities.
                    </p>
                  </div>
                </div>
              </Card>
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center text-primary">
                    <BookOpen className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold mb-2">Educational Purpose</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm">
                      Every analysis is designed to teach. Whether you're new to investing or a seasoned market watcher, Baron Wuffett's breakdowns help you think like a fundamental analyst.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* A Homage To Warren Buffett */}
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-center mb-4">A Homage To Warren Buffett</h2>
            <p className="text-gray-700 dark:text-gray-300 text-center max-w-2xl mx-auto mb-8">
              Baron Wuffett isn't Warren Buffett &mdash; but he's deeply inspired by him. The same philosophy of long-term value investing, patience, and fundamental analysis drives every one of Baron Wuffett's stock breakdowns.
            </p>
            {chartData.length > 0 && (() => {
              const lastPoint = chartData[chartData.length - 1];
              const brkbReturn = ((lastPoint.brkb - 10000) / 10000 * 100).toFixed(0);
              const spyReturn = ((lastPoint.spy - 10000) / 10000 * 100).toFixed(0);
              return (
              <Card className="p-6">
                <h3 className="text-lg font-bold text-center mb-4">
                  Growth of $10,000: Berkshire Hathaway vs S&P 500
                </h3>
                <div className="relative" style={{ width: '100%', height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 5, right: 85, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date: string) => date.substring(0, 4)}
                        interval={Math.floor(chartData.length / 6)}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        tickFormatter={formatDollar}
                        tick={{ fontSize: 12 }}
                        width={80}
                      />
                      <Tooltip
                        formatter={(value: number, name: string) => [
                          formatDollar(value),
                          name === 'brkb' ? 'BRK.B' : 'SPY',
                        ]}
                        labelFormatter={formatTooltipDate}
                      />
                      <Legend
                        formatter={(value: string) => (value === 'brkb' ? 'BRK.B (Berkshire Hathaway)' : 'SPY (S&P 500)')}
                      />
                      <Line
                        type="monotone"
                        dataKey="brkb"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={false}
                      />
                      <Line
                        type="monotone"
                        dataKey="spy"
                        stroke="#14b8a6"
                        strokeWidth={2}
                        dot={false}
                      />
                      <ReferenceDot
                        x={lastPoint.date}
                        y={lastPoint.brkb}
                        r={0}
                        label={<ReturnLabel value={`+${brkbReturn}%`} color="#6366f1" />}
                      />
                      <ReferenceDot
                        x={lastPoint.date}
                        y={lastPoint.spy}
                        r={0}
                        label={<ReturnLabel value={`+${spyReturn}%`} color="#14b8a6" />}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                  {/* Watermark */}
                  <div className="absolute bottom-14 right-8 flex items-center gap-1.5 opacity-30 pointer-events-none select-none">
                    <img
                      src="/assets/site-logo/cropped-neutral-logo.svg"
                      alt=""
                      className="w-5 h-5"
                    />
                    <span className="text-sm font-semibold" style={{ color: '#3b82f6' }}>
                      Wallstreetle
                    </span>
                  </div>
                </div>
              </Card>
              );
            })()}
          </div>

          {/* CTA */}
          <div className="text-center">
            <Card className="p-10 bg-gradient-to-br from-primary to-primary/80 text-white">
              <div className="flex justify-center mb-4">
                <AvatarSprite gesture="baron-wuffett-thumbs-up" size={96} />
              </div>
              <h2 className="text-3xl font-bold mb-4">
                See Baron Wuffett in Action
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Solve today's stock puzzle and unlock Baron Wuffett's expert analysis!
              </p>
              <Link
                to="/popular"
                className="inline-block px-10 py-4 bg-white text-primary rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors shadow-lg"
              >
                Play Today's Puzzle
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}
